import { useState, useCallback, useEffect, useMemo, memo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import CheckListConfigIndex from "./CheckListConfigIndex";
import { SaveCheckListConfig } from "../../../API_Service/Settings_Slice";
import { useGlobalLoader } from "../../../Common/GlobalLoader";
import {
  Button,
  Icon,
  Spinner,
  FormField,
  Textarea,
  InlineAlert,
  DrawerShell,
  getEmployeeID,
  getCompanyID,
  sanitizeLabName,
  normalizeSpaces,
} from "../../../Common/UIComponents";

const NAME_MAX_LENGTH = 100;

const INITIAL_FORM = {
  id: 0,
  name: "",
};

const CharCounter = ({ value, max }) => {
  const len = (value ?? "").length;
  if (len === 0) return null;
  const atLimit = len === max;
  return (
    <div
      className={`flex items-center gap-1 mt-1 text-xs ${atLimit ? "text-red-500 font-semibold" : "text-[#6e797a]"}`}
    >
      <span>
        {len}/{max}
      </span>
      {atLimit && <span className="text-red-500">— limit reached</span>}
    </div>
  );
};

// ─────────────────────────────────────────────
// FORM HOOK
// ─────────────────────────────────────────────

function useCheckListForm({
  onSubmit,
  onSuccess,
  onCancel,
  initialData,
  isEditMode,
}) {
  const { showLoader, hideLoader } = useGlobalLoader();

  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (initialData && !initialized) {
      setForm({
        id: initialData.id ?? 0,
        name: initialData.name ?? "",
      });
      setInitialized(true);
    }
  }, [initialData, initialized]);

  const validate = useCallback(() => {
    const next = {};
    if (!form.name.trim()) next.name = "MP Questions is required.";
    setErrors(next);
    return Object.keys(next).length === 0;
  }, [form]);

  const handleChange = useCallback(
    (field) => (e) => {
      let val =
        typeof e.target.value === "string"
          ? e.target.value.replace(/^\s+/, "")
          : e.target.value;
      if (field === "name" && typeof val === "string")
        val = sanitizeLabName(val);
      setForm((prev) => ({ ...prev, [field]: val }));
      setErrors((prev) => ({ ...prev, [field]: undefined }));
      setStatus("idle");
    },
    [],
  );

  const handleSubmit = useCallback(
    async (e) => {
      e?.preventDefault();
      if (!validate()) return;
      setStatus("loading");
      showLoader();
      try {
        const payload = {
          CheckListID: isEditMode ? (form.id ?? 0) : 0,
          CheckList: normalizeSpaces(form.name),
          CreatedBy: getEmployeeID(),
          CompanyId: getCompanyID(),
        };

        const result = await onSubmit(payload);
        if (result?.success) {
          setStatus("success");
          if (!isEditMode) {
            setForm(INITIAL_FORM);
            setInitialized(false);
          }
          onSuccess?.(payload);
        } else if (result?.duplicate) {
          setStatus("duplicate");
        } else {
          setStatus("error");
        }
      } catch {
        setStatus("error");
      } finally {
        hideLoader();
      }
    },
    [form, isEditMode, validate, onSubmit, onSuccess, showLoader, hideLoader],
  );

  const handleCancel = useCallback(() => {
    setForm(INITIAL_FORM);
    setInitialized(false);
    setErrors({});
    setStatus("idle");
    onCancel?.();
  }, [onCancel]);

  return { form, errors, status, handleChange, handleSubmit, handleCancel };
}

// ─────────────────────────────────────────────
// STATUS ALERTS
// ─────────────────────────────────────────────

const STATUS_ALERTS = {
  error: { type: "error", message: "Something went wrong. Please try again." },
  duplicate: {
    type: "warning",
    message: "A Checklist with this question already exists.",
  },
};

// ─────────────────────────────────────────────
// DRAWER FORM
// ─────────────────────────────────────────────

const CheckListDrawerForm = memo(
  ({
    isOpen,
    onClose,
    onSuccess,
    onSubmit,
    isEditMode = false,
    initialData = null,
  }) => {
    const { form, errors, status, handleChange, handleSubmit, handleCancel } =
      useCheckListForm({
        onSubmit,
        onSuccess,
        onCancel: onClose,
        initialData,
        isEditMode,
      });

    const isLoading = status === "loading";
    const alert =
      status === "success"
        ? {
            type: "success",
            message: isEditMode
              ? "Checklist updated successfully."
              : "Checklist saved successfully. You can add another one.",
          }
        : (STATUS_ALERTS[status] ?? null);

    const footer = (
      <>
        <Button variant="ghost" onClick={handleCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? (
            <>
              <Spinner /> Saving…
            </>
          ) : (
            <>
              <Icon name="save" className="text-[18px]" filled />
              {isEditMode ? "Update" : "Save"} Checklist
            </>
          )}
        </Button>
      </>
    );

    return (
      <DrawerShell
        isOpen={isOpen}
        onClose={onClose}
        title={isEditMode ? "Edit Checklist" : "Add Checklist"}
        subtitle="Build a reusable checklist made up of individual verification items."
        icon="checklist"
        footer={footer}
      >
        <form
          id="checklist-form"
          onSubmit={handleSubmit}
          className="space-y-6"
          noValidate
        >
          <FormField
            id="checklist-name"
            label="MP Questions"
            required
            error={errors.name}
          >
            <Textarea
              id="checklist-name"
              value={form.name}
              onChange={handleChange("name")}
              placeholder="Is the instrument calibrated as per the standard?"
              maxLength={NAME_MAX_LENGTH}
            />
            <CharCounter value={form.name} max={NAME_MAX_LENGTH} />
          </FormField>

          {alert && <InlineAlert type={alert.type} message={alert.message} />}
        </form>
      </DrawerShell>
    );
  },
);
CheckListDrawerForm.displayName = "CheckListDrawerForm";

// ─────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────

export default function CheckListConfigCreate({
  embedded = false,
  onCancel,
  onSuccess,
} = {}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const isEditMode = !embedded && location.state?.mode === "edit";
  const editData = embedded ? null : (location.state?.data ?? null);
  const isModalEntry = !embedded && !!location.state?.backgroundLocation;

  const [drawerOpen, setDrawerOpen] = useState(true);

  const initialEditData = useMemo(() => {
    if (isEditMode && editData) {
      return { id: editData.id ?? 0, name: editData.name ?? "" };
    }
    return null;
  }, [isEditMode, editData]);

  const handleSave = useCallback(
    async (payload) => {
      const res = await dispatch(SaveCheckListConfig(payload));
      const data = res?.payload?.data ?? res?.payload;

      if (Number(data) === -1) return { success: false, duplicate: true };

      if (data !== undefined && data !== null && Number(data) > 0) {
        // Embedded: hand the new id + name back to the caller so it can
        // refresh its list and auto-select the new entry.
        if (embedded) onSuccess?.(Number(data), payload?.CheckList ?? "");
        return { success: true, data };
      }

      return { success: false };
    },
    [dispatch, embedded, onSuccess],
  );

  // ── After a successful save ──
  //   EDIT (routed):    navigate straight back to the list with a success toast.
  //   CREATE (routed):  stay open on the drawer (inline success banner, form resets)
  //                     so the user can add another checklist right away.
  //   EMBEDDED:         handled entirely by handleSave -> onSuccess above.
  const handleFormSuccess = useCallback(() => {
    if (embedded) return;
    if (isEditMode) {
      navigate("/application-config", {
        state: { tab: "checklist", refresh: true, editSuccess: true },
      });
    }
  }, [embedded, isEditMode, navigate]);

  const handleClose = useCallback(() => {
    setDrawerOpen(false);
    if (embedded) {
      onCancel?.();
    } else {
      navigate("/application-config", {
        state: { tab: "checklist", refresh: true },
      });
    }
  }, [navigate, embedded, onCancel]);

  return (
    <>
      {isModalEntry && <CheckListConfigIndex />}

      <CheckListDrawerForm
        isOpen={drawerOpen}
        onClose={handleClose}
        onSuccess={handleFormSuccess}
        onSubmit={handleSave}
        initialData={initialEditData}
        isEditMode={isEditMode}
      />
    </>
  );
}
